import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import {
  forwardRef,
  motion,
  presenceExitStyles,
  system,
  useSize,
} from '@hover/blueprint';
import { fromEntries } from '@hover/utilities';
import type { TargetAndTransition, Transition } from 'framer-motion';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import type { Key } from 'react';
import React, { useMemo, useRef } from 'react';

import { fadePresence } from '../utils/motion';

type SpanProps = IntrinsicSystemProps<'span'>;

export interface AnimatedTextWidthProps
  extends Omit<MotionPropsFor<SpanProps>, 'animate'> {
  animate?: TargetAndTransition;
  /**
   * This required key determines when the `children` will be transitioned
   * between sizes. Pass a `boolean` to animate the text content visibility.
   */
  contentKey: Key | boolean;
  /**
   * Transition to apply when cross-fading the text content. The default is a
   * linear transition with a 0.2s duration.
   */
  textTransition?: Transition;
}

/**
 * Text properties that aren't inherited from their parent by default
 */
const inherit = fromEntries(
  (
    [
      'textTransform',
      'textDecoration',
    ] as const satisfies readonly (keyof SpanProps)[]
  ).map(key => [key, 'inherit'] as const),
);

/**
 * Prevent any sort of wrapping that would interfere with the width calculation
 */
const noWrap = {
  whiteSpace: 'nowrap',
  wordBreak: 'keep-all',
  overflowWrap: 'normal',
  textOverflow: 'clip',
} satisfies SpanProps;

export const AnimatedTextWidth = forwardRef<AnimatedTextWidthProps, 'span'>(
  (
    { animate = {}, children, contentKey, sx, textTransition, ...props },
    ref,
  ) => {
    const sizeRef = useRef<HTMLSpanElement>(null);

    const { width } = useSize(sizeRef) ?? { width: 0 };

    const textChildren = useMemo(() => {
      if (typeof contentKey === 'boolean') {
        return (
          <motion.span
            className="animated-text-width"
            {...fadePresence}
            key={contentKey ? 'visible' : 'hidden'}
          >
            {contentKey ? children : '\u2060'}
          </motion.span>
        );
      }

      return (
        <motion.span
          className="animated-text-width"
          {...fadePresence}
          key={contentKey}
        >
          {children}
        </motion.span>
      );
    }, [children, contentKey]);

    return (
      <motion.span
        {...inherit}
        alignItems="center"
        display="inline-flex"
        justifyContent="center"
        sx={{
          ...sx,
          ...presenceExitStyles('.animated-text-width', {
            top: 'unset',
            bottom: 'unset',
            width: 'unset',
            height: 'unset',
          }),
        }}
        {...props}
        {...noWrap}
        animate={{ ...animate, width }}
        ref={ref}
      >
        <system.span
          {...inherit}
          display="inline-block"
          {...noWrap}
          ref={sizeRef}
        >
          <MotionConfig transition={textTransition ?? { duration: 0.2 }}>
            <AnimatePresence initial={false} mode="popLayout">
              {textChildren}
            </AnimatePresence>
          </MotionConfig>
        </system.span>
      </motion.span>
    );
  },
);
